import React, { useState, useLayoutEffect, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { ReactComponent as CloseIcon } from "@/src/assets/icons/icon-close-black.svg";
import zIndex from "@/src/styles/zIndex";
import {
  OnboardingType,
  ONBOARDING_MEDIA,
  OnboardingSlide,
} from "@/src/constants/onboardingContents";
import { createPortal } from "react-dom";
import {
  markOnboardingDontShow,
  shouldShowOnboarding,
} from "@/src/utils/onboardingStorage";
import { useTranslation } from "react-i18next";

interface CommonOnboardingModalProps {
  type: OnboardingType;
  visible?: boolean;
  onClose?: () => void;
}

const CommonOnboardingModal: React.FC<CommonOnboardingModalProps> = ({
  type,
  visible = true,
  onClose,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const overflowCssValue = useRef<string>();
  const { t } = useTranslation();

  // 동적으로 슬라이드 콘텐츠 가져오기
  const slides: OnboardingSlide[] = [
    {
      ...(ONBOARDING_MEDIA[type]?.[0] || {}),
      title: t(`${type}.slide1.title`, { ns: "onboarding" }),
      description: t(`${type}.slide1.description`, { ns: "onboarding" }),
    },
  ];

  // 슬라이드 기능 임시 비활성화
  // const handleNext = () => {
  //   if (currentSlide < slides.length - 1) {
  //     setCurrentSlide(currentSlide + 1);
  //   }
  // };

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleStart = () => {
    handleClose();
  };

  const handleDontShowAgain = () => {
    markOnboardingDontShow(type);
    handleClose();
  };

  // const isLastSlide = currentSlide === slides.length - 1;
  const isLastSlide = true; // 슬라이드 기능 비활성화로 항상 마지막 슬라이드
  const currentSlideData = slides[currentSlide];

  useEffect(() => {
    // 로컬스토리지 확인 후 모달 표시 여부 결정
    const shouldShow = visible && shouldShowOnboarding(type);
    setIsVisible(shouldShow);
  }, [type, visible]);

  useLayoutEffect(() => {
    if (isVisible) {
      const originalOverflow = window.getComputedStyle(document.body).overflow;
      overflowCssValue.current = originalOverflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = overflowCssValue.current ?? "visible";
      };
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return createPortal(
    <>
      <StyledOverlay onClick={handleClose} />
      <StyledDialogContent>
        <ModalContent>
          <Header>
            <CloseButton onClick={handleClose}>
              <CloseIcon />
            </CloseButton>
          </Header>

          <ContentSection>
            <TextSection>
              <Title>{currentSlideData.title}</Title>
              <Description>{currentSlideData.description}</Description>
            </TextSection>
          </ContentSection>

          <MediaContainer>
            {currentSlideData.videoUrl ? (
              <Video
                src={currentSlideData.videoUrl}
                autoPlay
                loop
                muted
                playsInline
              />
            ) : currentSlideData.lottieUrl ? (
              <LottieWrapper>
                <DotLottieReact
                  src={currentSlideData.lottieUrl}
                  loop
                  autoplay
                  style={{ width: "1000px", height: "480px" }}
                />
              </LottieWrapper>
            ) : currentSlideData.imgUrl ? (
              <Image
                src={currentSlideData.imgUrl}
                alt={currentSlideData.title}
              />
            ) : null}
          </MediaContainer>

          <Footer>
            {/* 슬라이드 인디케이터 임시 비활성화 */}
            {/* <SlideIndicator>
            {currentSlide + 1}/{slides.length}
          </SlideIndicator> */}
            <div />

            <FooterButtons>
              <DontShowAgainButton onClick={handleDontShowAgain}>
                {t("dontShowAgain", { ns: "common" })}
              </DontShowAgainButton>

              {/* 슬라이드 기능 비활성화로 항상 시작하기 버튼만 표시 */}
              <StartButton onClick={handleStart}>
                {t("start", { ns: "common" })}
              </StartButton>
              {/* {isLastSlide ? (
              <StartButton onClick={handleStart}>{t("start", { ns: "common" })}</StartButton>
            ) : (
              <NextButton onClick={handleNext}>{t("next", { ns: "common" })}</NextButton>
            )} */}
            </FooterButtons>
          </Footer>
        </ModalContent>
      </StyledDialogContent>
    </>,
    document.body
  );
};

const overlayKeyframe = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const StyledOverlay = styled.div`
  background: rgba(0, 0, 0, 0.2);
  position: fixed;
  inset: 0;
  animation: ${overlayKeyframe} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  z-index: ${zIndex.dialog};
`;

const StyledDialogContent = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  max-width: 1000px;
  height: 90vh;
  max-height: 720px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  animation: ${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  z-index: ${zIndex.dialog};
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 95vw;
    height: 95vh;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  align-self: stretch;
  padding: 16px 16px 16px 24px;
  justify-content: flex-end;
  align-items: center;
  display: flex;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 6px;
  background: white;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  overflow: hidden;
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  display: flex;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0px 2px 4px rgba(5, 29, 57, 0.15);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ContentSection = styled.div`
  align-self: stretch;
  padding: 0 40px 32px 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: flex;

  @media (max-width: 768px) {
    padding: 0 24px 24px 24px;
  }

  @media (max-width: 480px) {
    padding: 0 16px 16px 16px;
  }
`;

const TextSection = styled.div`
  text-align: center;
  gap: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MediaContainer = styled.div`
  align-self: stretch;
  height: 480px;
  position: relative;
  background: #c9def7;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    box-shadow: 0px -4px 8px rgba(28, 61, 97, 0.08) inset;
    z-index: 1;
  }
`;

const Video = styled.video`
  width: 1000px;
  height: 480px;
  object-fit: cover;
`;

const Image = styled.img`
  width: 1000px;
  height: 480px;
  object-fit: cover;
`;

const LottieWrapper = styled.div`
  width: 1000px;
  height: 480px;
  display: flex;
  align-items: center;
  justify-content: center;

  > div {
    width: 1000px;
    height: 480px;
  }
`;

const Title = styled.h2`
  font-family: "Pretendard";
  font-style: normal;
  font-weight: 700;
  font-size: 28px;
  line-height: 38px;
  text-align: center;
  color: #000000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    line-height: 28px;
  }
`;

const Description = styled.p`
  font-family: "Pretendard";
  font-style: normal;
  font-weight: 500;
  font-size: 17px;
  line-height: 24px;
  text-align: center;
  color: #222729;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 22px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 16px 20px;
  }

  @media (max-width: 480px) {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
  }
`;

const SlideIndicator = styled.div`
  font-family: "Pretendard";
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  color: #aeb7bc;
`;

const FooterButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 480px) {
    width: 100%;
    flex-direction: column;
  }
`;

const DontShowAgainButton = styled.button`
  min-width: 120px;
  height: 40px;
  padding: 10px 16px;
  background: white;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  overflow: hidden;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;

  font-family: "Pretendard";
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  color: #222729;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0px 2px 4px rgba(5, 29, 57, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  }

  @media (max-width: 768px) {
    min-width: 100px;
    height: 36px;
    font-size: 14px;
    padding: 8px 12px;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const Button = styled.button`
  min-width: 120px;
  height: 40px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: "Pretendard";
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  text-align: center;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;

  @media (max-width: 768px) {
    min-width: 100px;
    height: 36px;
    font-size: 14px;
    padding: 8px 12px;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const NextButton = styled(Button)`
  background: #1c3d61;
  color: white;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  overflow: hidden;

  &:hover {
    background: #153049;
    transform: translateY(-1px);
    box-shadow: 0px 2px 4px rgba(5, 29, 57, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  }
`;

const StartButton = styled(Button)`
  background: #1c3d61;
  color: white;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  overflow: hidden;

  &:hover {
    background: #153049;
    transform: translateY(-1px);
    box-shadow: 0px 2px 4px rgba(5, 29, 57, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  }
`;

export default CommonOnboardingModal;
